import { useState, useMemo } from 'react';

type SortDirection = 'asc' | 'desc';

interface UseTableDataOptions<T> {
    data: T[] | undefined;
    defaultSortKey: string;
    defaultSortDirection?: SortDirection;
    sortFn: (a: T, b: T, sortKey: string, direction: SortDirection) => number;
}

export function useTableData<T>({
    data,
    defaultSortKey,
    defaultSortDirection = 'asc',
    sortFn,
}: UseTableDataOptions<T>) {
    const [sortColumn, setSortColumn] = useState<string>(defaultSortKey);
    const [sortDirection, setSortDirection] = useState<SortDirection>(defaultSortDirection);

    const handleSort = (column: string) => {
        if (sortColumn === column) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortColumn(column);
            setSortDirection('asc');
        }
    };

    const sortedData = useMemo(() => {
        if (!data) return [];

        return [...data].sort((a, b) => sortFn(a, b, sortColumn, sortDirection));
    }, [data, sortColumn, sortDirection, sortFn]);

    return {
        sortedData,
        sortColumn,
        sortDirection,
        handleSort,
    };
}
